import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Calendar, Clock, MapPin, Download, Home, List, Info } from "lucide-react";
import { CardDescription } from "@/components/ui/card";
import { useEffect, useState, useRef, useMemo } from "react";
import { pushGtmEvent } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { useI18n } from "@/i18n/I18nProvider";
import { formatCurrency } from "@/lib/currency";
import { Seo } from "@/components/seo/Seo";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

type BookingModeMeta = {
  booking_mode?: string;
  approval_status?: string | null;
  vendor_response_deadline_at?: string | null;
};

function paymentSuccessHeading(meta: BookingModeMeta | null, paymentMethod?: string) {
  const mode = meta?.booking_mode;
  const pendingVendor =
    (mode === "request" || mode === "urgent") &&
    (meta?.approval_status === "pending_vendor" || meta?.approval_status == null);
  if (mode === "instant" && meta?.approval_status === "not_required") {
    return {
      title: "Prenotazione confermata!",
      subtitle:
        paymentMethod === "invoice"
          ? "L’ordine è registrato. Riceverai aggiornamenti via email."
          : "Pagamento ricevuto: la prenotazione è confermata.",
    };
  }
  if (pendingVendor) {
    return {
      title: "Richiesta inviata",
      subtitle:
        paymentMethod === "card"
          ? "Pagamento ricevuto. L’azienda deve ancora confermare la prenotazione; ti avvisiamo non appena risponde."
          : "L’azienda deve ancora confermare la prenotazione; ti avvisiamo non appena risponde.",
    };
  }
  return {
    title: "Richiesta inviata!",
    subtitle: "Pagamento ricevuto. La prenotazione è in attesa di conferma da parte dell’azienda.",
  };
}

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const {
    bookingData: stateBookingData,
    company: stateCompany,
    service: stateService,
    pricing: statePricing,
    orderId: stateOrderId,
    payment: statePayment,
    bookingModeMeta: stateBookingModeMeta,
  } = location.state || {};
  const { t } = useI18n();
  const [emailForAccount, setEmailForAccount] = useState(stateBookingData?.email || "");
  const [bookingData, setBookingData] = useState<any>(stateBookingData || null);
  const [company, setCompany] = useState<any>(stateCompany || null);
  const [service, setService] = useState<any>(stateService || null);
  const [pricing, setPricing] = useState<any>(statePricing || null);
  const [orderId, setOrderId] = useState<string | undefined>(stateOrderId);
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [modeMeta, setModeMeta] = useState<BookingModeMeta | null>(
    (stateBookingModeMeta as BookingModeMeta | undefined) ?? null
  );
  const bookingCompleteSent = useRef(false);
  const guestPrepareSent = useRef(false);

  const resolvedBookingId = useMemo(() => {
    const fromState = (location.state as { bookingId?: string } | null)?.bookingId;
    const fromQuery = new URLSearchParams(location.search).get("booking");
    return fromState || fromQuery || null;
  }, [location.state, location.search]);

  const pushBookingCompleteOnce = (payload: Record<string, unknown>) => {
    if (bookingCompleteSent.current) return;
    bookingCompleteSent.current = true;
    pushGtmEvent("booking_complete", payload);
  };

  // booking_complete (GTM/GA4) — once per successful visit
  useEffect(() => {
    const fromNav = !!(stateBookingData || stateOrderId);
    const bookingQ = new URLSearchParams(location.search).get("booking");
    if (fromNav) {
      pushBookingCompleteOnce({
        source: "navigation_state",
        order_id: stateOrderId,
        payment_free: statePayment?.free === true,
      });
      return;
    }
    if (!bookingQ) {
      pushBookingCompleteOnce({ source: "minimal_success" });
    }
  }, []);

  useEffect(() => {
    const bookingQ = new URLSearchParams(location.search).get("booking");
    if (bookingQ && bookingData) {
      pushBookingCompleteOnce({
        source: "stripe_redirect",
        booking_id: bookingQ,
        order_id: orderId,
      });
    }
  }, [bookingData, orderId, location.search]);

  useEffect(() => {
    const fromNav = !!(stateBookingData || stateOrderId);
    const bookingQ = new URLSearchParams(location.search).get("booking");
    if (fromNav || !bookingQ) return;
    const t = window.setTimeout(() => {
      pushBookingCompleteOnce({
        source: "stripe_redirect",
        booking_id: bookingQ,
        detail: "deferred_or_empty",
      });
    }, 6000);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (guestPrepareSent.current || !resolvedBookingId) return;
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) return;
        guestPrepareSent.current = true;
        await fetch("/api/ops?action=guest_prepare", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ booking_id: resolvedBookingId }),
        });
      } catch {}
    })();
  }, [resolvedBookingId]);

  // NEW: when arriving from the draft flow we get ?session_id=cs_...&booking_draft=...
  // The webhook creates the booking row asynchronously, so we poll a server-side
  // lookup endpoint until the booking shows up (or we give up after ~30s).
  useEffect(() => {
    if (bookingData) return;
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const draftIdParam = params.get('booking_draft');
    if (!sessionId && !draftIdParam) return;
    if (params.get('booking')) return; // legacy path handled below
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 15; // ~30s at 2s interval
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        if (sessionId) {
          const r = await fetch('/api/stripe?action=lookup_booking_by_session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sessionId }),
          });
          if (r.ok) {
            const j = await r.json();
            if (j?.ok && j?.booking_id) {
              // Hydrate the page using the legacy ?booking=<id> path by updating the URL.
              const u = new URL(window.location.href);
              u.searchParams.set('booking', j.booking_id);
              // Keep session_id: a guest (no Supabase session) needs it for the
              // ownership-gated summary fallback after the reload below.
              u.searchParams.delete('booking_draft');
              window.history.replaceState(null, '', u.toString());
              // Trigger the legacy effect by reloading the query parser.
              window.location.replace(u.toString());
              return;
            }
          }
        }
      } catch {}
      if (attempts < maxAttempts && !cancelled) {
        window.setTimeout(tick, 2000);
      }
    };
    tick();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If we arrive from a Stripe redirect (no location.state), hydrate by ?booking=<id>.
  // Logged-in buyers can read their own booking directly (RLS allows it). Guests
  // are blocked by RLS, so we fall back to an ownership-gated server summary keyed
  // by the Stripe session id (?session_id=cs_...). Both paths produce the same shape.
  useEffect(() => {
    if (bookingData) return;
    const params = new URLSearchParams(location.search);
    const bookingId = params.get('booking');
    const sessionId = params.get('session_id');
    if (!bookingId) return;

    const applySummary = (s: any) => {
      if (!s) return;
      setModeMeta(s.modeMeta || null);
      setOrderId(s.orderId);
      setCompany(s.company);
      setService(s.service);
      setPricing(s.pricing);
      if (s.receiptUrl) setReceiptUrl(s.receiptUrl);
      setBookingData({
        ...s.bookingData,
        date: s.bookingData?.date ? new Date(s.bookingData.date) : new Date(),
      });
    };

    // Map a raw bookings row (client read) into the same shape the server returns.
    // Recurring parent: total_amount / customer_fee_cents are per-visit, so multiply
    // by occurrences to show what the customer actually paid for the whole series.
    const summaryFromRow = (data: any) => {
      const isParent = !!data.is_recurring_parent;
      const occ = isParent ? Math.max(1, Number(data.recurring_occurrences) || 1) : 1;
      const perVisitService = Number(data.total_amount || 0);
      const perVisitFeeCents = Math.max(0, Number(data.customer_fee_cents || 0));
      const serviceTotal = perVisitService * occ;
      const feeCents = perVisitFeeCents * occ;
      const vendorCovers = !!data.pricing_snapshot?.vendor_covers_customer_fee;
      const customerPaid = serviceTotal + (vendorCovers ? 0 : feeCents / 100);
      const sa = data.service_addresses;
      const joinedAddr = sa ? [sa.address_line1, sa.address_line2, `${sa.postal_code || ''} ${sa.city || ''}`.trim(), sa.country].filter(Boolean).join(', ') : '';
      const address = (data.service_address_text || '').trim() || joinedAddr || '';
      const fullName = data.customer_name || '';
      return {
        orderId: data.booking_number || data.id,
        modeMeta: {
          booking_mode: data.booking_mode ?? undefined,
          approval_status: data.approval_status ?? null,
          vendor_response_deadline_at: data.vendor_response_deadline_at ?? null,
        },
        company: {
          name: data.companies?.company_name || 'Vendor',
          image: data.companies?.logo_url || '/assets/placeholders/logo-placeholder.svg',
          email: data.companies?.email || undefined,
          legalName: data.companies?.legal_name || data.companies?.company_name || undefined,
          vatNumber: data.companies?.vat_number || undefined,
          sdiCode: data.companies?.sdi_code || undefined,
          pecEmail: data.companies?.pec_email || undefined,
        },
        service: { name: data.services?.name || 'Service' },
        pricing: {
          servicePrice: serviceTotal,
          serviceFee: feeCents / 100,
          vendorCoversFee: vendorCovers,
          total: customerPaid,
          occurrences: occ,
          perVisit: perVisitService,
        },
        bookingData: {
          date: data.scheduled_date || null,
          time: data.scheduled_time || '09:00',
          serviceAddress: address,
          firstName: fullName.split(' ')[0] || '',
          lastName: fullName.split(' ').slice(1).join(' ') || '',
          email: data.customer_email || emailForAccount || '',
          phone: data.customer_phone || '',
        },
      };
    };

    (async () => {
      try {
        const { data, error } = await supabase
          .from('bookings')
          .select('id, booking_number, scheduled_date, scheduled_time, total_amount, customer_fee_cents, pricing_snapshot, service_address_text, customer_name, customer_email, customer_phone, booking_mode, approval_status, vendor_response_deadline_at, payment_method, is_recurring_parent, recurring_occurrences, services(name), companies(company_name,logo_url,email,legal_name,vat_number,sdi_code,pec_email), service_addresses(address_line1,address_line2,city,postal_code,country)')
          .eq('id', bookingId)
          .maybeSingle();
        if (!error && data) {
          applySummary(summaryFromRow(data));
          try {
            const p = await supabase.from('payments').select('receipt_url').eq('booking_id', bookingId).order('created_at', { ascending: false }).limit(1).maybeSingle();
            if (p.data?.receipt_url) setReceiptUrl(p.data.receipt_url);
          } catch {}
          return;
        }
        // Guest: anonymous RLS hides the row -> use the ownership-gated server summary.
        if (sessionId) {
          const r = await fetch('/api/stripe?action=get_booking_summary', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sessionId, booking_id: bookingId }),
          });
          if (r.ok) {
            const j = await r.json();
            if (j?.ok && j?.summary) applySummary(j.summary);
          }
        }
      } catch {}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!bookingData) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-success/20 rounded-full mb-4">
            <CheckCircle className="h-10 w-10 text-success" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">{t('payments.success.title','Richiesta inviata!')}</h1>
          <p className="text-muted-foreground">{t('payments.success.subtitle','Pagamento ricevuto. La prenotazione è in attesa di conferma da parte dell’azienda.')}</p>
          <div className="mt-6">
            <Button onClick={() => navigate('/profile')} variant="outline">{t('payments.btn.my_orders','Le mie prenotazioni')}</Button>
          </div>
        </div>
      </div>
    );
  }

  const payMethod = String(statePayment?.method || "");
  const { title: successTitle, subtitle: successSubtitle } = paymentSuccessHeading(modeMeta, payMethod);
  const showVendorApprovalHint =
    !!modeMeta &&
    (modeMeta.booking_mode === "request" || modeMeta.booking_mode === "urgent") &&
    (modeMeta.approval_status === "pending_vendor" || modeMeta.approval_status == null);

  const handleAddToCalendar = () => {
    const startDate = new Date(bookingData.date);
    const [hours, minutes] = bookingData.time.split(':');
    startDate.setHours(parseInt(hours), parseInt(minutes));
    
    const endDate = new Date(startDate);
    endDate.setHours(startDate.getHours() + 3); // Assume 3-hour service

    const title = `${service.name} - ${company.name}`;
    const details = `Service: ${service.name}\nProvider: ${company.name}\nAddress: ${bookingData.serviceAddress}`;
    
    // Google Calendar URL
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${startDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z/${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z&details=${encodeURIComponent(details)}&location=${encodeURIComponent(bookingData.serviceAddress)}`;
    
    window.open(googleCalendarUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo title="Conferma prenotazione | Halomora" noindex canonicalPath="/payment-success" />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-success/20 rounded-full mb-4">
              <CheckCircle className="h-10 w-10 text-success" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">{successTitle}</h1>
            <p className="text-lg text-muted-foreground">
              {statePayment?.message || successSubtitle}
            </p>
          </div>

          {showVendorApprovalHint && (
            <Alert className="mb-8 text-left border-amber-500/35 bg-amber-500/[0.06]">
              <Info className="h-4 w-4 text-amber-700 dark:text-amber-400" />
              <AlertTitle>In attesa di conferma dell&apos;azienda</AlertTitle>
              <AlertDescription>
                {modeMeta?.vendor_response_deadline_at ? (
                  <>
                    Per questa richiesta l&apos;azienda ha tempo fino al{" "}
                    <strong>{format(new Date(String(modeMeta.vendor_response_deadline_at)), "PPp")}</strong> per accettare o
                    rifiutare. Ti invieremo un aggiornamento via email.
                  </>
                ) : (
                  <>L&apos;azienda confermerà la prenotazione a breve. Ti invieremo un aggiornamento via email.</>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Order Details */}
          <Card className="bg-gradient-card border-0 shadow-strong mb-8">
            <CardHeader>
              <CardTitle className="text-foreground">{t('payments.order.confirmation','Conferma ordine')}</CardTitle>
              <Badge variant="outline" className="w-fit">
                {t('payments.order.id','ID Ordine')}: {orderId}
              </Badge>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Service Summary */}
              <div className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg">
                <img
                  src={company.image}
                  alt={company.name}
                  className="w-16 h-16 rounded-xl object-cover shadow-medium"
                />
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-foreground">{service.name}</h3>
                  <p className="text-muted-foreground mt-1">{company.name}{company.email ? ` • ${company.email}` : ''}</p>
                  <p className="text-2xl font-bold text-primary mt-2">{formatCurrency(Number(pricing.total || 0))}</p>
                  {(Number(pricing?.serviceFee || 0) > 0 || Number(pricing?.servicePrice || 0) > 0) && (
                    <div className="mt-2 text-sm text-muted-foreground space-y-0.5">
                      {Number(pricing?.occurrences || 1) > 1 && (
                        <div className="flex justify-between gap-4">
                          <span>Per visita × {Number(pricing.occurrences)} occorrenze</span>
                          <span>{formatCurrency(Number(pricing.perVisit || 0))}</span>
                        </div>
                      )}
                      <div className="flex justify-between gap-4">
                        <span>{Number(pricing?.occurrences || 1) > 1 ? 'Totale servizio (serie)' : 'Servizio'}</span>
                        <span>{formatCurrency(Number(pricing?.servicePrice || pricing?.total || 0))}</span>
                      </div>
                      {Number(pricing?.serviceFee || 0) > 0 && (
                        <div className="flex justify-between gap-4">
                          <span>Fee piattaforma {pricing?.vendorCoversFee ? <em className="text-xs">(coperta dal fornitore)</em> : null}</span>
                          <span>
                            {pricing?.vendorCoversFee
                              ? <span className="line-through opacity-60">{formatCurrency(Number(pricing.serviceFee || 0))}</span>
                              : formatCurrency(Number(pricing.serviceFee || 0))
                            }
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Fiscal details (if provided by vendor) */}
              {(company.vatNumber || company.pecEmail || company.sdiCode) && (
                <div className="border-t border-border/50 pt-4">
                  <h4 className="font-semibold text-foreground mb-3">Dati fiscali fornitore</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {company.legalName && <div><span className="text-muted-foreground">Ragione sociale:</span> <span className="font-medium">{company.legalName}</span></div>}
                    {company.vatNumber && <div><span className="text-muted-foreground">Partita IVA:</span> <span className="font-medium">{company.vatNumber}</span></div>}
                    {company.sdiCode && <div><span className="text-muted-foreground">Codice SDI:</span> <span className="font-medium">{company.sdiCode}</span></div>}
                    {company.pecEmail && <div><span className="text-muted-foreground">PEC:</span> <span className="font-medium">{company.pecEmail}</span></div>}
                  </div>
                </div>
              )}

              {/* Appointment Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">{t('payments.field.date','Data')}</p>
                      <p className="text-muted-foreground">{format(bookingData.date, "EEEE, MMMM d, yyyy")}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">{t('payments.field.time','Orario')}</p>
                      <p className="text-muted-foreground">{bookingData.time}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <p className="font-medium text-foreground">{t('payments.field.address','Indirizzo del servizio')}</p>
                      <p className="text-muted-foreground">{bookingData.serviceAddress}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="border-t border-border/50 pt-4">
                <h4 className="font-semibold text-foreground mb-3">{t('payments.contact.info','Informazioni di contatto')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm items-start">
                  <div>
                    <p className="text-muted-foreground">{t('field.name','Nome')}</p>
                    <p className="font-medium text-foreground">{bookingData.firstName} {bookingData.lastName}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-muted-foreground">Email</p>
                    <p className="font-medium text-foreground break-words whitespace-normal">{bookingData.email}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-muted-foreground">{t('field.phone','Telefono')}</p>
                    <p className="font-medium text-foreground break-words whitespace-normal">{bookingData.phone}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What's Next */}
          <Card className="bg-gradient-card border-0 shadow-medium mb-8">
            <CardHeader>
              <CardTitle className="text-foreground">{t('payments.next.title','E adesso?')}</CardTitle>
              <CardDescription>
                {t('payments.next.subtitle','Crea un account per visualizzare le prenotazioni, riprogrammare e scaricare le ricevute in un unico posto.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-muted-foreground">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <p>{t('payments.next.email','Riceverai a breve un’email di conferma pagamento e aggiornamenti sullo stato.')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <p>{t('payments.next.vendor_contact','L’azienda confermerà (o rifiuterà) la richiesta. Se non può accettare, riceverai un rimborso automatico.')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <p>{t('payments.next.track','Puoi seguire lo stato dell’ordine nella sezione "Le mie prenotazioni".')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <p>{t('payments.next.change','Se devi apportare modifiche, contattaci almeno 24 ore prima.')}</p>
                </div>
              </div>
              <div className="mt-6 p-4 border rounded-xl bg-muted/30">
                <p className="text-sm text-foreground">
                  {t(
                    "payments.next.activation_email",
                    "Ti abbiamo inviato un'email per attivare il tuo profilo Halomora in un minuto: basta scegliere una password e gestire prenotazioni, indirizzi e ricevute."
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {t(
                    "payments.next.activation_note",
                    "Non trovi l'email? Controlla lo spam. Puoi anche attivare il profilo più tardi: la prenotazione resta valida anche senza account."
                  )}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Button 
              onClick={handleAddToCalendar}
              variant="outline" 
              className="flex items-center gap-2"
            >
              <Calendar className="h-4 w-4" />
              {t('payments.btn.add_calendar','Aggiungi al calendario')}
            </Button>
            
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              disabled={!receiptUrl}
              onClick={() => { if (receiptUrl) window.open(receiptUrl, '_blank'); }}
            >
              <Download className="h-4 w-4" />
              {t('payments.btn.download_receipt','Scarica ricevuta')}
            </Button>
            
            <Button 
              onClick={() => navigate("/profile")}
              variant="outline" 
              className="flex items-center gap-2"
            >
              <List className="h-4 w-4" />
              {t('payments.btn.my_orders','Le mie prenotazioni')}
            </Button>
            
            <Button 
              onClick={() => navigate("/")}
              variant="hero" 
              className="flex items-center gap-2"
            >
              <Home className="h-4 w-4" />
              {t('payments.btn.return_home','Torna alla Home')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;